import React, { useState, useEffect } from 'react';

interface RSVPFormProps {
  onRsvpChange: (hasConfirmedAttendees: boolean) => void;
}

interface RSVPFormData {
  dietaryRestrictions: string;
  message: string;
  invitees: {
    name1: boolean;
    name2: boolean;
    name3: boolean;
    name4: boolean;
    name5: boolean;
  };
}

interface InvitationData {
  name1?: string;
  name2?: string;
  name3?: string;
  name4?: string;
  name5?: string;
  name1_rsvp?: boolean;
  name2_rsvp?: boolean;
  name3_rsvp?: boolean;
  name4_rsvp?: boolean;
  name5_rsvp?: boolean;
  dietaryRestrictions?: string;
  message?: string;
}

const RSVPForm: React.FC<RSVPFormProps> = ({ onRsvpChange }) => {
  const [formData, setFormData] = useState<RSVPFormData>({
    dietaryRestrictions: '',
    message: '',
    invitees: {
      name1: true,
      name2: true,
      name3: true,
      name4: true,
      name5: true
    }
  });

  const [hasSubmittedOnce, setHasSubmittedOnce] = useState(false);
  const [invitationData, setInvitationData] = useState<InvitationData>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Check if there are any confirmed attendees
  const checkConfirmedAttendees = (data: any, isSubmitted: boolean) => {
    // Only count as confirmed if they've submitted the form or have existing RSVPs
    if (isSubmitted || hasSubmittedOnce) {
      // Only check invitees that exist in the invitation data
      const existingInvitees = Object.entries(data.invitees).filter(([name]) => 
        name in invitationData && name.startsWith('name')
      );
      
      const hasConfirmed = existingInvitees.length > 0 && 
        existingInvitees.some(([_, isAttending]) => isAttending === true);
      
      onRsvpChange(hasConfirmed);
      return hasConfirmed;
    }
    return false;
  };

  useEffect(() => {
    const fetchInvitationData = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const id = urlParams.get('id');

        if (!id) {
          setError('No invitation ID provided');
          setIsLoading(false);
          return;
        }

        const response = await fetch(`https://plxzfm4mxf.execute-api.us-east-1.amazonaws.com/default/UpdateBirthdayRsvpTable?id=${id}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch invitation details');
        }

        const data = await response.json();
        setInvitationData(data);
        
        const initialFormData = {
          dietaryRestrictions: data.dietaryRestrictions || '',
          message: data.message || '',
          invitees: {
            name1: data.name1_rsvp !== undefined ? data.name1_rsvp : true,
            name2: data.name2_rsvp !== undefined ? data.name2_rsvp : true,
            name3: data.name3_rsvp !== undefined ? data.name3_rsvp : true,
            name4: data.name4_rsvp !== undefined ? data.name4_rsvp : true,
            name5: data.name5_rsvp !== undefined ? data.name5_rsvp : true
          }
        };
        
        setFormData(initialFormData);
        
        // If any RSVPs exist in the data, consider it as submitted
        const hasExistingRsvps = ['name1_rsvp', 'name2_rsvp', 'name3_rsvp', 'name4_rsvp', 'name5_rsvp']
          .some(name => data[name] === true);
        
        if (hasExistingRsvps) {
          setHasSubmittedOnce(true);
          // Directly check if any existing invitees are confirmed
          const existingInvitees = Object.entries(initialFormData.invitees)
            .filter(([name]) => name in data && name.startsWith('name'));
          const hasConfirmed = existingInvitees.length > 0 && 
            existingInvitees.some(([_, isAttending]) => isAttending === true);
          onRsvpChange(hasConfirmed);
        }

      } catch (err) {
        setError('Error loading invitation details');
        console.error('Error fetching invitation:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitationData();
  }, [onRsvpChange]);

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false);
      }, 5000); // Hide after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const id = urlParams.get('id');

      if (!id) {
        throw new Error('No invitation ID provided');
      }

      // Filter invitees to only include those that exist in invitationData
      const filteredInvitees = Object.entries(formData.invitees)
        .filter(([name]) => name in invitationData && name.startsWith('name'))
        .reduce((acc, [name, value]) => ({ ...acc, [name]: value }), {});

      const response = await fetch(`https://plxzfm4mxf.execute-api.us-east-1.amazonaws.com/default/UpdateBirthdayRsvpTable?id=${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dietaryRestrictions: formData.dietaryRestrictions,
          message: formData.message,
          invitees: filteredInvitees,
          id
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit RSVP');
      }

      setHasSubmittedOnce(true);
      checkConfirmedAttendees({ ...formData, invitees: filteredInvitees }, true);
      setShowSuccess(true);
    } catch (error) {
      setSubmitError('Failed to submit RSVP. Please try again.');
      console.error('Error submitting RSVP:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInviteeChange = (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFormData = {
      ...formData,
      invitees: {
        ...formData.invitees,
        [name]: e.target.checked
      }
    };
    setFormData(newFormData);
  };

  if (isLoading) {
    return (
      <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-xl shadow-lg">
        <div className="text-center">
          <p className="text-gray-600">Loading invitation details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded-xl shadow-lg">
        <div className="text-center">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-4 p-6 bg-white rounded-xl shadow-lg border-2 border-sky-700">
      {showSuccess && (
        <div className="fixed top-4 left-0 right-0 mx-auto w-fit z-50">
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">Thank you for your RSVP! 🚂</span>
          </div>
        </div>
      )}

      <div className="text-center mb-4">
        <h2 className="text-3xl font-bold text-sky-800 mb-2">🚂 RSVP for Daniel’s 2nd Birthday! 🚂</h2>
        <p className="text-gray-700 italic mb-2">All aboard — Daniel is turning two!</p>
        <div className="text-sm text-gray-700 space-y-1">
          <div>
            <span className="font-semibold">When:</span> June 13, 2026 · 10:30am – 1:00pm
          </div>
          <div>
            <span className="font-semibold">Where:</span> Seattle Children’s Museum (Seattle Center)
          </div>
        </div>
        <p className="mt-3 text-[11px] text-gray-500 leading-snug max-w-sm mx-auto border-t border-gray-100 pt-3">
          Full-day museum admission is included for all guests.<br />Birthday activities and food start at 11:00am.
        </p>
      </div>

      {submitError && (
        <div className="mb-4 p-4 bg-rose-50 border border-rose-300 text-rose-800 rounded">
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Invitees Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Who's Ready to Party?</h3>
          {invitationData.name1 && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="name1"
                checked={formData.invitees.name1}
                onChange={handleInviteeChange('name1')}
                className="rounded border-gray-300 text-sky-700 focus:ring-sky-700"
              />
              <label htmlFor="name1" className="ml-2 block text-sm text-gray-900">
                {invitationData.name1}
              </label>
            </div>
          )}
          {invitationData.name2 && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="name2"
                checked={formData.invitees.name2}
                onChange={handleInviteeChange('name2')}
                className="rounded border-gray-300 text-sky-700 focus:ring-sky-700"
              />
              <label htmlFor="name2" className="ml-2 block text-sm text-gray-900">
                {invitationData.name2}
              </label>
            </div>
          )}
          {invitationData.name3 && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="name3"
                checked={formData.invitees.name3}
                onChange={handleInviteeChange('name3')}
                className="rounded border-gray-300 text-sky-700 focus:ring-sky-700"
              />
              <label htmlFor="name3" className="ml-2 block text-sm text-gray-900">
                {invitationData.name3}
              </label>
            </div>
          )}
          {invitationData.name4 && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="name4"
                checked={formData.invitees.name4}
                onChange={handleInviteeChange('name4')}
                className="rounded border-gray-300 text-sky-700 focus:ring-sky-700"
              />
              <label htmlFor="name4" className="ml-2 block text-sm text-gray-900">
                {invitationData.name4}
              </label>
            </div>
          )}
          {invitationData.name5 && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id="name5"
                checked={formData.invitees.name5}
                onChange={handleInviteeChange('name5')}
                className="rounded border-gray-300 text-sky-700 focus:ring-sky-700"
              />
              <label htmlFor="name5" className="ml-2 block text-sm text-gray-900">
                {invitationData.name5}
              </label>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="dietaryRestrictions" className="block text-sm font-medium text-gray-700">
            Dietary Restrictions <span className="text-[10px] text-gray-600">(party will feature cake and pizza)</span>
          </label>
          <input
            type="text"
            id="dietaryRestrictions"
            name="dietaryRestrictions"
            value={formData.dietaryRestrictions}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-700 focus:ring-sky-700"
            placeholder="Any food allergies or preferences?"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-medium text-gray-700">
            Message for Daniel 🚂
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-sky-700 focus:ring-sky-700"
            placeholder="Share your birthday wishes!"
          />
        </div>

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-sky-700 hover:bg-sky-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-700 transform transition-transform hover:scale-105 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Sending...' : 'Count Me In!'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RSVPForm; 